/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
 * The base colors for the Material Dashboard 2 React (DARK).
 * Renix redesign (Phase 1): dark mirror of the light neutrals — surfaces #171E29 on
 * canvas #10151D; semantic colors brightened to hold AA on dark. Preset machinery
 * (`info` + `gradients.info` + `brand`) is injected by buildDarkTheme and left untouched.
 */

const colors = {
  background: {
    default: "#10151D",
    sidenav: "#171E29",
    card: "#171E29",
    paper: "#171E29",
  },

  text: {
    main: "#E8ECF2",
    focus: "#9AA6B5",
  },

  transparent: {
    main: "transparent",
  },

  white: {
    main: "#ffffff",
    focus: "#ffffff",
  },

  black: {
    light: "#000000",
    main: "#000000",
    focus: "#000000",
  },

  primary: {
    main: "#4F93D1",
    focus: "#3B7CB8",
  },

  secondary: {
    main: "#7b809a",
    focus: "#8f93a9",
  },

  info: {
    main: "#0072D4",
    focus: "#005EB3",
  },

  success: {
    main: "#3FA46F",
    focus: "#2E8659",
  },

  brand: {
    primary: "#0072D4",
    primaryFocus: "#005EB3",
    secondary: "#F56242",
    secondaryFocus: "#D94E33",
  },

  warning: {
    main: "#D99A2B",
    focus: "#B8811F",
  },

  error: {
    // #E0655A measured 4.49:1 against its own 8% StatusBadge tint — one hundredth under AA.
    main: "#E0675C",
    focus: "#C74E44",
  },

  light: {
    main: "#1C2530",
    focus: "#232E3C",
  },

  dark: {
    main: "#E8ECF2",
    focus: "#CBD3DE",
  },

  grey: {
    100: "#f8f9fa",
    200: "#f0f2f5",
    300: "#dee2e6",
    400: "#ced4da",
    500: "#adb5bd",
    600: "#6c757d",
    700: "#495057",
    800: "#343a40",
    900: "#212529",
  },

  gradients: {
    primary: {
      main: "#EC407A",
      state: "#D81B60",
    },

    secondary: {
      main: "#747b8a",
      state: "#495361",
    },

    info: {
      main: "#339AEB",
      state: "#0072D4",
    },

    success: {
      main: "#4ade80",
      state: "#16a34a",
    },

    warning: {
      main: "#FFA726",
      state: "#FB8C00",
    },

    error: {
      main: "#EF5350",
      state: "#E53935",
    },

    light: {
      main: "#EBEFF4",
      state: "#CED4DA",
    },

    dark: {
      main: "#323a54",
      state: "#1a2035",
    },
  },

  socialMediaColors: {
    facebook: {
      main: "#3b5998",
      dark: "#344e86",
    },

    twitter: {
      main: "#55acee",
      dark: "#3ea1ec",
    },

    instagram: {
      main: "#125688",
      dark: "#0e456d",
    },

    linkedin: {
      main: "#0077b5",
      dark: "#00669c",
    },

    pinterest: {
      main: "#cc2127",
      dark: "#b21d22",
    },

    youtube: {
      main: "#e52d27",
      dark: "#d41f1a",
    },

    vimeo: {
      main: "#1ab7ea",
      dark: "#13a3d2",
    },

    slack: {
      main: "#3aaf85",
      dark: "#329874",
    },

    dribbble: {
      main: "#ea4c89",
      dark: "#e73177",
    },

    github: {
      main: "#24292e",
      dark: "#171a1d",
    },

    reddit: {
      main: "#ff4500",
      dark: "#e03d00",
    },

    tumblr: {
      main: "#35465c",
      dark: "#2a3749",
    },
  },

  badgeColors: {
    primary: {
      background: "#f8b3ca",
      text: "#cc084b",
    },

    secondary: {
      background: "#d7d9e1",
      text: "#6c757d",
    },

    info: {
      background: "#CCE3F7",
      text: "#004A8A",
    },

    success: {
      background: "#dcfce7",
      text: "#14532d",
    },

    warning: {
      background: "#ffd59f",
      text: "#c87000",
    },

    error: {
      background: "#fcd3d0",
      text: "#f61200",
    },

    light: {
      background: "#ffffff",
      text: "#c7d3de",
    },

    dark: {
      background: "#8097bf",
      text: "#1e2e4a",
    },
  },

  coloredShadows: {
    primary: "#e91e62",
    secondary: "#110e0e",
    info: "rgba(0, 114, 212, 0.4)",
    success: "rgba(22, 163, 74, 0.4)",
    warning: "#ff9900",
    error: "#f44336",
    light: "#adb5bd",
    dark: "#404040",
  },

  inputBorderColor: "#d2d6da",

  // ===== Renix redesign tokens (additive — see docs/redesign_plan.md §6.B.2) =====
  // Dark mirror of the light neutrals; semantic colors tuned for AA on dark surfaces.
  canvas: { main: "#10151D" },
  surface: { main: "#171E29" },
  ink: { main: "#E8ECF2", secondary: "#9AA6B5" },
  // See the light mirror: `main` = decorative hairline, `control` = form-control outline
  // (1.4.11 needs 3:1 against the surface), `strong` = its hover step.
  borderColor: { main: "#2A3442", control: "#5F6A77", strong: "#7A8593" },
  // `onSurface` is the text/link shade (see theme/functions/accessibleAccent). Static
  // fallback only; buildDarkTheme replaces all three per active preset.
  accent: {
    main: "#4F93D1",
    focus: "#3B7CB8",
    soft: "rgba(79,147,209,0.14)",
    onSurface: "#4F93D1",
  },

  tabs: {
    indicator: { boxShadow: "#ddd" },
  },
};

export default colors;
